<template>
    <div>
        <rkm-form
            ref="Form"
            show-legend
        >
            <rkm-row>

                <rkm-col cols="6">
                    <rkm-form-date-picker
                        range
                        :range-inline="false"
                        start-label="Período de"
                        end-label="Até"
                        v-model="model.periodo"
                        :max-date="data.form.dataAtual"
                        :start-validations="validations.periodo.start"
                        :end-validations="validations.periodo.end"
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-select
                        :options="agendamentoExame"
                        label="Grupo"
                        show-default-option
                        text-field="nome"
                        v-model="model.exameId"
                        value-field="id"
                        @change="setListagem(); loadFields()"
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-select
                        :options="data.form.profissionaisAssinatura"
                        label="Profissional"
                        show-default-option
                        text-field="nome"
                        v-model="model.profissionalId"
                        :validations="validations.profissional"
                        value-field="id"
                        @change="setListagem()"
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-select
                        :options="laboratorios"
                        label="Laboratório"
                        show-default-option
                        default-option-text="Todos"
                        text-field="nome"
                        v-model="model.laboratorioId"
                        value-field="id"
                        @change="setListagem()"
                    />
                </rkm-col>

                <rkm-col cols="3" >
                    <rkm-form-input-autocomplete
                        :searchFields="localPaciente.autocompleteFieldsPaciente.paciente"
                        :repository="repositories.paciente"
                        v-model="paciente"
                        :text-field="(item) =>  item.cidadao.cns
                            ? `${this.formatCns(item.cidadao.cns)} - ${item.cidadao.nome}`
                            : item.cidadao.nome"
                        label="Paciente"
                        uppercase
                        @select="setListagem(); loadFields()"
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-date-picker
                        label="Data de nascimento"
                        v-model="model.dataNascimento"
                        :max-date="data.form.dataAtual"
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-input
                        label="Prontuário"
                        v-model="model.prontuario"
                        number
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row class="flex-nowrap">

                <rkm-col cols="auto">
                    <rkm-form-checkbox-group
                        label="Status"
                        v-model="model.statusId"
                        :options="model.status"
                        :validations="validations.status"
                        text-field="nome"
                        value-field="id"
                        buttons
                        button-color="outline-primary"
                        @change="setListagem(); loadFields()"
                    />
                </rkm-col>

                <rkm-col class="flex-grow-1">
                    <rkm-form-input
                        label="Número do agendamento"
                        v-model="model.agendamentoNumero"
                        number
                    />
                </rkm-col>

                <rkm-col cols="auto">
                    <rkm-button
                        :label="false"
                        color="primary"
                        @click="$refs.Form.validate(search)"
                    >
                        Buscar
                    </rkm-button>

                    <rkm-button
                        :label="false"
                        @click="resetForm()"
                    >
                        Limpar
                    </rkm-button>
                </rkm-col>
            </rkm-row>
        </rkm-form>

        <rkm-row
            class="flex-nowrap"
            v-if="itemsNotSave"
        >

            <rkm-col
                v-for="status in formStatus"
                :key="status.id"
                class="flex-grow-0 pt-2 pb-1"
            >
                <rkm-row
                    class="flex-nowrap"
                >
                    <rkm-col class="flex-grow-0 pr-0">
                        <div v-bind="getSubtitleProps(status)"></div>
                    </rkm-col>

                    <rkm-col class="flex-grow-1 text-nowrap">
                        <span>{{ status.nome }}</span>
                    </rkm-col>
                </rkm-row>
            </rkm-col>
        </rkm-row>

        <rkm-table
            v-if="itemsNotSave"
            :items="itemsNotSave || items"
            :fields="fields"
            :tbody-tr-attr="handleRowAttr"
            hide-filters
        >

            <template v-slot:cell(pacienteNome)="{ item }">
                {{ item.pacienteNome }}

                <template v-if="item.pacienteNomeSocial">
                    <template v-if="item.pacientePreferenciaNomeId === enums.preferenciaNome.NOME_SOCIAL">
                        <strong>({{ item.pacienteNomeSocial }})</strong>
                    </template>
                </template>
            </template>

            <template v-slot:cell(actions)="{ item }">

                <rkm-button
                    color="primary"
                    small
                    title="Resultado"
                    :disabled="item.statusId == enums.statusAgendamento.ASSINADO"
                    @click.stop="$router.push({ name: 'exame-laboratorial-assinatura-eletronica-create',
                        params: { id: item.agendamentoExameId, profissionalId: model.profissionalId } })"
                >
                    <rkm-icon icon="share" />
                </rkm-button>
            </template>
        </rkm-table>
    </div>
</template>

<script>
import { mapActions } from 'vuex'
import { repository, error, object, formats, data, stringCase } from '@/mixins'

// Moment.
import moment from 'moment'

// Enums.
import statusAgendamento from '@/enums/exame-laboratorial/statusAgendamento'
import preferenciaNome from '@/enums/cadastro/cidadao/preferenciaNome'

export default {
    name: 'exame-laboratorial-assinatura-eletronica-index',
    computed: {
        fields() {
            const fields = [
                {
                    key: "data",
                    label: "Data da coleta",
                    class: 'col-min text-center',
                    sortable: true
                },
                {
                    key: "horaInicial",
                    label: "Horário",
                    class: 'col-min text-center',
                    sortable: true
                },
                {
                    key: "agendamentoNumero",
                    label: "Agendamento",
                    class: 'col-min text-center',
                    sortable: true
                },
                {
                    key: "pacienteNome",
                    label: "Paciente",
                    sortable: true
                },
                {
                    key: "pacienteProntuario",
                    class: 'col-min text-center',
                    label: "Prontuário",
                    sortable: true
                },
                {
                    key: "exameNome",
                    label: "Exame",
                    sortable: true
                },
                {
                    key: "unidadeNome",
                    label: "Unidade solicitante",
                    sortable: true
                },
                {
                    key: "infraestruturaNome",
                    label: "Local de atendimento",
                    sortable: true
                },
                {
                    key: 'actions',
                    label: 'Ações',
                    class: 'col-min'
                }
            ]

            return fields
        },
        formStatus() {
            const status =  [
                {
                    "id": this.enums.statusAgendamento.REALIZADO,
                    "nome": "COLETA REALIZADA",
                    "cor": "warning",
                    "cor_hover": null
                },
                {
                    "id": this.enums.statusAgendamento.PENDENTE_ASSINATURA,
                    "nome": "PENDENTE DE ASSINATURA",
                    "cor": "primary",
                    "cor_hover": null
                },
                {
                    "id": this.enums.statusAgendamento.ASSINADO,
                    "nome": "ASSINADO",
                    "cor": "success",
                    "cor_hover": null
                },
                {
                    "id": this.enums.statusAgendamento.NAO_REALIZADO,
                    "nome": "NÃO REALIZADO",
                    "cor": "danger",
                    "cor_hover": null
                },
                {
                    "id": this.enums.statusAgendamento.NOVA_COLETA,
                    "nome": "NOVA COLETA",
                    "cor": "secondary",
                    "cor_hover": null
                },
            ]

            return status
        },
        autocompletesRequestParams() {

            return {
                exame: {
                    queryString: {
                        tipo_id: this.tipoExameId,
                        exame_grupo: true,
                    }
                }
            }
        },
        fieldsAutoComplete() {

            return {
                autocompletes: {
                    exame: [
                        { key: 'codigo', searchable: true },
                        { key: 'nome', searchable: true }
                    ],
                }
            }
        },
        validations() {

            return {
                periodo: {
                    start: { required: true },
                    end: { required: true },
                },
                status: { required: true },
                profissional: { required: true },
            }
        },
        infraestruturas() {

            this.setInfraestrutura()

            if (this.model.unidadeId && this.unidadeSolicitante.length > 0) {

                let infraestruturaPrincipal = this.unidadeSolicitante.find(u => u.unidadeId == this.model.unidadeId).infraestruturas

                if (!this.model.unidadeDisabled) {

                    this.handleInfraestrutura(infraestruturaPrincipal)
                }

                return infraestruturaPrincipal
            }

            return []
        },
    },
    created() {

        this.repositories = {
            assinatura: this.getConectaSusRepository('exameLaboratorial.assinaturaEletronica'),
            paciente: this.getConectaSusRepository('cadastro.sujeitoAtencao.paciente'),
        },

        this.load();
    },
    data() {
        return {
            model: {
                exame: null,
                exameId: null,
                exameNome: null,
                pacienteId: null,
                profissionalId: null,
                unidadeId: null,
                infraestruturaId: null,
                prontuario: null,
                agendamentoNumero: null,
                status: [],
                statusId: [],
                pertenceGrupo: null,
                unidadeNome: null,
                infraestruturaNome: null,
                dataNascimento: null,
                laboratorioId: null,
                periodo: {
                    start: null,
                    end: null,
                },
            },
            saveAgendamento: {
                agendamentos: [],
            },
            paciente: null,
            unidadeSolicitante: [],
            agendamentoExame: [],
            laboratorios: [],
            itemsNotSave: [],
            localPaciente: {
                autocompleteFieldsPaciente: {
                    paciente: [
                        { key: 'cidadao.nome', searchable: true },
                    ],
                },
            },
            enums: {
                statusAgendamento,
                preferenciaNome
            },
            unwatchers: {
                modelPeriodoEnd: null,
                modelPeriodoStart: null,
            },
        }
    },
    mixins: [repository, error, object, formats, data, stringCase],
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {

            this.model.periodo = this.data.form.periodo

            const profissional = this.data.form.profissionaisAssinatura.find(profissional =>
                profissional.id == this.data.form.profissional.id)

            if (profissional) {

                this.model.profissionalId = profissional.id
            }

            this.model.statusId = [
                this.enums.statusAgendamento.REALIZADO,
                this.enums.statusAgendamento.PENDENTE_ASSINATURA,
                this.enums.statusAgendamento.ASSINADO,
                this.enums.statusAgendamento.NAO_REALIZADO,
                this.enums.statusAgendamento.NOVA_COLETA,
            ]

            this.model.status = [
                {
                    "id": this.enums.statusAgendamento.REALIZADO,
                    "nome": "COLETA REALIZADA",
                },
                {
                    "id": this.enums.statusAgendamento.PENDENTE_ASSINATURA,
                    "nome": "PENDENTE DE ASSINATURA",
                },
                {
                    "id": this.enums.statusAgendamento.ASSINADO,
                    "nome": "ASSINADO",
                },
                {
                    "id": this.enums.statusAgendamento.NAO_REALIZADO,
                    "nome": "NÃO REALIZADO",
                },
                {
                    "id": this.enums.statusAgendamento.NOVA_COLETA,
                    "nome": "NOVA COLETA",
                },
            ]

            this.$nextTick()
                .then(() => this.loadFields())
                .then(() => this.addWatchers())
        },
        handleRowAttr(item, type) {
            if (type === 'row') {
                if (item.statusId == this.enums.statusAgendamento.ASSINADO) {
                    return {
                        style: `
                            border-left: 10px solid #28a745;
                            background-color: #28a74515;
                        `
                    }
                }
                else if (item.statusId == this.enums.statusAgendamento.PENDENTE_ASSINATURA) {
                    return {
                        style: `
                            border-left: 10px solid #007bff;
                            background-color: #007bff15;
                        `
                    }
                }
                else if (item.statusId == this.enums.statusAgendamento.REALIZADO) {
                    return {
                        style: `
                            border-left: 10px solid #ffc107;
                            background-color: #ffc10715;
                        `
                    }
                }
                else if (item.statusId == this.enums.statusAgendamento.NAO_REALIZADO) {
                    return {
                        style: `
                            border-left: 10px solid #dc3545;
                            background-color: #dc354515;
                        `
                    }
                }
                else if (item.statusId == this.enums.statusAgendamento.NOVA_COLETA) {
                    return {
                        style: `
                            border-left: 10px solid #6c757d;
                            background-color: #6c757d15;
                        `
                    }
                }
            }
        },
        prepareData() {

            if (this.itemsNotSave && this.itemsNotSave.length > 0) {

                this.saveAgendamento.agendamentos = this.clone(this.itemsNotSave)

                const notSave = this.saveAgendamento

                return this.camelObjectKeysToSnakeRecursive(notSave)
            } else {

                const model = this.clone(this.data.model)

                return this.camelObjectKeysToSnakeRecursive(model)
            }
        },
        getSubtitleProps(status) {

            const classes = ['content-subtitle-item-color']
            const style = {}

            classes.push(`list-group-item-${status.cor}`)

            return { class: classes, style }
        },
        search() {

            this.setLoading()

            this.repositories.assinatura.list(this.getSearchParams())
                .then(({ data }) => {

                    this.itemsNotSave = data.model.agendamentos

                    this.setLaboratorios(data.model.laboratorios)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        validate() {

            return this.$refs.Form.validate()
        },
        getSearchParams() {

            const params = this.clone(this.model)

            if (this.paciente) {

                params.pacienteId = this.paciente.id
                params.pacienteNome = this.paciente.cidadao.nome
            }

            if (this.model.exameId) {

                params.pertenceGrupo = this.agendamentoExame.find(u => u.id == this.model.exameId).pertenceGrupo
                params.exameNome = this.agendamentoExame.find(u => u.id == this.model.exameId).nome
            }

            if (this.model.dataNascimento) {

                params.dataNascimento = moment(this.model.dataNascimento).format('YYYY-MM-DD')
            }

            this.formateDates(params.periodo, ['start', 'end'], 'YYYY-MM-DD')

            return this.camelObjectKeysToSnakeRecursive(params)
        },
        resetForm() {

            this.model.exame = null
            this.model.exameId = null
            this.paciente = null
            this.model.agendamentoNumero = null
            this.model.prontuario = null
            this.model.laboratorioId = null
            this.itemsNotSave = null

            this.setDefaultValues()

            this.$nextTick(() => this.$refs.Form.resetValidator())
        },
        setDefaultValues() {

            this.model.periodo.start = moment(this.data.form.dataAtual, 'DD/MM/YYYY').toDate()
            this.model.periodo.end = moment(this.data.form.dataAtual, 'DD/MM/YYYY').toDate()
        },
        loadFields() {

            this.setLoading()

            this.repositories.assinatura.loadFields(this.getSearchParams())
                .then(({ data }) => {

                    this.loadExame(data.form.grupos)

                    this.setLaboratorios(data.form.laboratorios)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        loadExame(grupos) {

            const exameCadastrado = []

           grupos.map(exame => {

                const exameInserido = exameCadastrado.find(u => u.id
                    === exame.exameGrupoId && u.pertenceGrupo === exame.pertenceGrupo)

                if (!exameInserido) {

                    exameCadastrado.push({

                        id: exame.exameGrupoId,
                        nome: exame.exameGrupoNome,
                        pertenceGrupo: exame.pertenceGrupo,
                    })
                }
            })

            if (exameCadastrado) {

                exameCadastrado.sort((a,b) => (a.nome > b.nome) ? 1 : ((b.nome > a.nome) ? -1 : 0));
            }

            this.agendamentoExame = exameCadastrado
        },
        setLaboratorios(laboratorios) {

            this.laboratorios = laboratorios

            if (this.model.laboratorioId && !this.laboratorios.find(laboratorio => laboratorio.id == this.model.laboratorioId)) {

                this.model.laboratorioId = null
                this.setListagem()
            }
        },
        setListagem() {

            this.itemsNotSave = null
        },
        addWatchers() {

            this.addModelPeridoStartWatcher()
            this.addModelPeridoEndWatcher()
        },
        addModelPeridoStartWatcher() {

            this.unwatchers.modelPeriodoStart = this.$watch('model.periodo.start', function () {

                this.setListagem()

                if (this.model.periodo.start && this.model.periodo.end) {

                    this.loadFields()
                }
            })
        },
        addModelPeridoEndWatcher() {

            this.unwatchers.modelPeriodoEnd = this.$watch('model.periodo.end', function () {

                this.setListagem()

                if (this.model.periodo.start && this.model.periodo.end) {

                    this.loadFields()
                }
            })
        },
    },
    activated() {
        this.search()
    },
    props: {
        data: { type: Object, required: true }
    },
}
</script>

<style scoped>
.table-td {
    background: #f1f1f1;
    padding: 0 10px;
    height: 35px;
    border-top: 1px solid #ccc;
    font-weight: 600;
}
.table-border-top {
    border-top: 0;
    height: 30px;
    padding: 0;
}
.table-width {
    width: 100%;
}
</style>

<style scoped>
p {
    margin: 0px;
}
.warning {
    color: var(--warning);
}
.primary {
    color: var(--primary);
}
.success {
    color: var(--success);
}
.danger {
    color: var(--danger);
}
.secondary {
    color: var(--secondary);
}
.content-subtitle-item-color {
    width: 30px;
    height: 20px;
    border: 1px solid rgba(0, 0, 0, 0.15);
}
</style>